import { useMemo, useState } from 'react';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { DarwinButton, DarwinFlex, DarwinListView } from 'components/darwin-ui';
import { getReportsColumns } from './columns';
import useTableControls from 'hooks/useTableControls';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';
import { useGetReports, useGetFiltersData } from 'api/services/reportsService';
import { Report } from 'types/reportTypes';
import TransactionDetailsModal from './TransactionDetailsModal';
import { IconName } from 'components/darwin-ui/Icon';
import { DeleteTransactionModal } from './DeleteTransactionModal';
import { GenerateReportDrawer } from 'components/ui/reports/GenerateReportDrawer';
import { FilterOutlined } from '@ant-design/icons';
import { FiltersDrawer } from 'components/darwin-ui/FiltersDrawer';
import {
  ReportsFilters,
  useReportsFilters,
} from 'contexts/ReportsFiltersContext';
import type { FilterField } from 'components/darwin-ui/FiltersDrawer';
import { REPORTS_FILTERS_NAME } from './constants/reports';

export type ColumnAction = {
  permission?: boolean;
  key: string;
  name?: IconName;
  label?: string;
  onClick?: (transaction: Report) => void;
};

export const Reports = () => {
  const { t } = useTranslation();
  const checkPermission = usePermissionChecker();
  const [reportsDrawerOpen, setReportsDrawerOpen] = useState(false);

  const viewReportsPermission = checkPermission(
    PermissionModules.ReportingAndAnalytics,
    PermissionActions.Read
  );

  const [selectedTransactionDetails, setTransactionDetails] =
    useState<Report | null>(null);

  const isTransactionDetailsModalOpen = !!selectedTransactionDetails;

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls({
    defaultSortBy: 'transactionDate',
    defaultSortOrder: 'DESC',
  });

  const { filters, setFilters, activeFiltersCount, clearFilters } =
    useReportsFilters();

  const {
    data: reportsResult,
    isLoading: isLoadingReports,
    isFetching: isFetchingReports,
    isPending: isPendingReports,
  } = useGetReports({
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
    event: filters.event,
    products: filters.products,
    lotCode: filters.lotCode,
    refDocType: filters.refDocType,
    refDocNumber: filters.refDocNumber,
    since: filters.since,
    until: filters.until,
  });

  const isLoading =
    viewReportsPermission === null ||
    isLoadingReports ||
    isFetchingReports ||
    isPendingReports;

  const [transactionToDelete, setTransactionToDelete] = useState<Report>();

  const actions = useMemo<ColumnAction[]>(
    () => [
      {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: setTransactionToDelete,
        permission: true,
      },
    ],
    [t]
  );

  // filter drawer
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const { data: filtersData } = useGetFiltersData();

  const fields = useMemo<FilterField[]>(() => {
    return [
      {
        name: REPORTS_FILTERS_NAME.FROM_DATE,
        label: t('reports.filters-drawer.fields.from-date'),
        type: 'datePicker',
        placeholder: '',
        allowClear: true,
        span: 1,
      },
      {
        name: REPORTS_FILTERS_NAME.TO_DATE,
        label: t('reports.filters-drawer.fields.to-date'),
        type: 'datePicker',
        placeholder: '',
        allowClear: true,
        span: 1,
      },
      {
        name: REPORTS_FILTERS_NAME.EVENT,
        label: t('reports.filters-drawer.fields.event'),
        type: 'select',
        options: filtersData?.data.events?.map(e => ({
          ...e,
          label: e.translationKey ? t(e.translationKey) : e.label,
        })),
        allowClear: true,
        span: 2,
      },
      {
        name: REPORTS_FILTERS_NAME.PRODUCT,
        label: t('reports.filters-drawer.fields.product'),
        type: 'select',
        mode: 'multiple',
        options: filtersData?.data.products?.map(p => ({
          ...p,
          label: p.translationKey ? t(p.translationKey) : p.label,
        })),
        showSearch: true,
        allowClear: true,
        span: 2,
      },
      {
        name: REPORTS_FILTERS_NAME.LOT_CODE,
        label: t('reports.filters-drawer.fields.lot-code'),
        type: 'select',
        mode: 'multiple',
        options: filtersData?.data.lotCode,
        showSearch: true,
        allowClear: true,
        span: 2,
      },
      {
        name: REPORTS_FILTERS_NAME.REF_DOC_TYPE,
        label: t('reports.filters-drawer.fields.ref-doc-type'),
        type: 'select',
        options: filtersData?.data.refDocType?.map(r => ({
          ...r,
          label: r.translationKey ? t(r.translationKey) : r.label,
        })),
        allowClear: true,
        span: 2,
      },
      {
        name: REPORTS_FILTERS_NAME.REF_DOC_NUMBER,
        label: t('reports.filters-drawer.fields.ref-doc-number'),
        type: 'select',
        mode: 'multiple',
        options: filtersData?.data.refDocNumber,
        showSearch: true,
        allowClear: true,
        span: 2,
      },
    ];
  }, [
    filtersData?.data.events,
    filtersData?.data.lotCode,
    filtersData?.data.products,
    filtersData?.data.refDocNumber,
    filtersData?.data.refDocType,
    t,
  ]);

  return (
    <>
      <ContentHeader
        title={t('transactions.title')}
        onSaveTitle={t('reports.download')}
        onSave={() => setReportsDrawerOpen(true)}
      />

      <TransactionDetailsModal
        open={isTransactionDetailsModalOpen}
        onCancel={() => setTransactionDetails(null)}
        transactionId={selectedTransactionDetails?.transactionId}
      />

      <DarwinListView<Report>
        data={reportsResult?.data ?? []}
        columns={getReportsColumns(t, actions)}
        isLoading={isLoading}
        header={{
          placeholderWidth: 600,
          actions: (
            <DarwinFlex justify="flex-end">
              <DarwinButton
                icon={<FilterOutlined />}
                onClick={() => setIsFiltersDrawerOpen(true)}
                style={{
                  borderRadius: 6,
                  margin: '8px 12px',
                  width: 'fit-content',
                }}
              >
                {t('reports.filters-drawer.button', {
                  filterCount: activeFiltersCount,
                })}
              </DarwinButton>
            </DarwinFlex>
          ),
        }}
        table={{ onChange: onTableChange }}
        behavior={{ onRowClick: setTransactionDetails }}
        pagination={{
          total: reportsResult?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
      />

      <DeleteTransactionModal
        displayDeleteModal={!!transactionToDelete}
        onCancel={() => setTransactionToDelete(undefined)}
        transaction={transactionToDelete}
      />

      <GenerateReportDrawer
        open={reportsDrawerOpen}
        onClose={() => setReportsDrawerOpen(false)}
        hasFilters={!!searchQuery}
      />

      <FiltersDrawer<ReportsFilters>
        title={t('reports.filters-drawer.title')}
        subtitle={t('reports.filters-drawer.subtitle')}
        open={isFiltersDrawerOpen}
        onClose={() => {
          setIsFiltersDrawerOpen(false);
        }}
        behavior={{
          clearOnClose: false,
          closeOnApply: true,
          clearOnApply: false,
          clearOnCloseIcon: false,
          clearOnCancel: true,
        }}
        actions={{
          apply: {
            onApply: values => {
              setFilters(values);
              setCurrentPage(1);
            },
          },
          cancel: {
            text: t('reports.filters-drawer.clear-filters'),
          },
        }}
        fields={fields}
        clearFilters={clearFilters}
      />
    </>
  );
};
